import React, { useState, useEffect } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaFileInvoice } from 'react-icons/fa';
import { getDtesByReceiptId, DteRecord } from '../../../../../../../../../../crud/payments_api/dte';
import DteCard from './components/DteCard/DteCard';
import CreateDte, { CreatedDteData } from './components/CreateDte/CreateDte';
import RenderPdf from './components/DteCard/components/RenderPdf/RenderPdf';
import './DteInfo.scss';

interface DteInfoProps {
  receiptId: number;
  localId: number;
}

const DteInfo: React.FC<DteInfoProps> = ({ receiptId, localId }) => {
  const [dtes, setDtes] = useState<DteRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [autoOpenPdf, setAutoOpenPdf] = useState<CreatedDteData | null>(null);

  const loadDtes = async () => {
    setLoading(true);
    try {
      const response = await getDtesByReceiptId(localId, receiptId, false);
      if (response.data.success) {
        const loadedDtes = response.data.dtes || [];
        setDtes(loadedDtes);
        // Auto-open CreateDte form if no DTEs exist
        if (loadedDtes.length === 0) {
          setShowCreateForm(true);
        }
      }
    } catch (error) {
      console.error('Error loading DTEs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDtes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptId, localId]);

  const handleCreateSuccess = (createdDte?: CreatedDteData) => {
    setShowCreateForm(false);
    loadDtes();
    // Auto-open PDF viewer with the newly created DTE
    if (createdDte) {
      setAutoOpenPdf(createdDte);
    }
  };

  if (loading) {
    return (
      <div className="dte-loading">
        <Spinner animation="border" />
        <p>Cargando DTEs...</p>
      </div>
    );
  }

  if (dtes.length === 0) {
    return (
      <>
        <div className="dte-empty-state">
          <FaFileInvoice className="empty-icon" />
          <h5>No hay DTEs asociados</h5>
          <p>Generando documento tributario electrónico...</p>
        </div>
        
        <CreateDte
          show={showCreateForm}
          receiptId={receiptId}
          localId={localId}
          onSuccess={handleCreateSuccess}
          onCancel={() => setShowCreateForm(false)}
        />

        {/* Auto-open PDF viewer after DTE creation */}
        {autoOpenPdf && (
          <RenderPdf
            show={!!autoOpenPdf}
            onHide={() => setAutoOpenPdf(null)}
            dteId={autoOpenPdf.dteId}
            localId={autoOpenPdf.localId}
            folio={autoOpenPdf.folio}
            tipoDte={autoOpenPdf.tipoDte}
            defaultFormat="80mm"
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="dte-list-container">
        <div className="dte-list-header">
          <h5>Documentos Tributarios Electrónicos</h5>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => {
              console.log('Nuevo DTE button clicked');
              setShowCreateForm(true);
            }}
          >
            <FaPlus className="me-2" />
            Nuevo DTE
          </Button>
        </div>
        <div className="dte-cards-grid">
          {dtes.map((dte) => (
            <DteCard 
              key={dte.dte_id}
              dte={dte}
              onClick={() => {
                console.log('DTE clicked:', dte);
              }}
            />
          ))}
        </div>
      </div>
      
      <CreateDte
        show={showCreateForm}
        receiptId={receiptId}
        localId={localId}
        onSuccess={handleCreateSuccess}
        onCancel={() => setShowCreateForm(false)}
      />

      {/* Auto-open PDF viewer after DTE creation */}
      {autoOpenPdf && (
        <RenderPdf
          show={!!autoOpenPdf}
          onHide={() => setAutoOpenPdf(null)}
          dteId={autoOpenPdf.dteId}
          localId={autoOpenPdf.localId}
          folio={autoOpenPdf.folio}
          tipoDte={autoOpenPdf.tipoDte}
          defaultFormat="80mm"
        />
      )}
    </>
  );
};

export default DteInfo;
